import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { APIAuthRequestOTP, APIAuthLogin } from '../utilities/APIs';
import './Login.css';
import { UnikLog } from '../assets';

const Login = () => {
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [showOtpSection, setShowOtpSection] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { login, isAuthenticated } = useAuth();

    useEffect(() => {
        // Check if user is already logged in
        if (isAuthenticated) {
            navigate('/');
        }
    }, [navigate, isAuthenticated]);

    const showMessage = (text, type) => {
        setMessage(text);
        setMessageType(type);
    };

    // Auto-append @unik.ac.ug to email if not present
    const formatEmail = (inputEmail) => {
        if (inputEmail && !inputEmail.includes('@')) {
            return inputEmail + '@unik.ac.ug';
        }
        return inputEmail;
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        // Remove @unik.ac.ug if user tries to type it manually
        const cleanValue = value.replace('@unik.ac.ug', '');
        setEmail(cleanValue);
    };    const handleOtpChange = (index, value) => {
        // Only allow digits
        if (!/^\d*$/.test(value)) return;
        
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, ''); // Remove non-digits
        
        if (pastedData.length >= 6) {
            const newOtp = pastedData.slice(0, 6).split('');
            // Fill remaining slots with empty strings if needed
            while (newOtp.length < 6) {
                newOtp.push('');
            }
            setOtp(newOtp);
            
            // Focus the last filled input or the last input if all are filled
            const lastFilledIndex = Math.min(pastedData.length - 1, 5);
            setTimeout(() => {
                const targetInput = document.getElementById(`otp-${lastFilledIndex}`);
                if (targetInput) targetInput.focus();
            }, 0);
        }
    };

    const handleOtpKeyDown = (index, e) => {
        // Handle backspace to focus previous input
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            if (prevInput) prevInput.focus();
        }
    };

    const requestOtp = async () => {
        if (!email) {
            showMessage('Please enter your UNIK email.', 'danger');
            return;
        }

        setIsLoading(true);
        showMessage('Sending verification code...', 'info');
        
        try {
            const formattedEmail = formatEmail(email);
            console.log('Requesting OTP for email:', formattedEmail);
            console.log('API endpoint:', APIAuthRequestOTP);
            
            const response = await fetch(APIAuthRequestOTP, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email: formattedEmail })
            });

            console.log('OTP request response status:', response.status);
            const data = await response.json();
            console.log('OTP request response data:', data);

            if (response.ok) {
                showMessage(`Verification code sent to ${formattedEmail}`, 'success');
                setShowOtpSection(true);
                // Focus first OTP input
                setTimeout(() => {
                    const firstInput = document.getElementById('otp-0');
                    if (firstInput) firstInput.focus();
                }, 100);
            } else {
                // Handle specific error cases
                if (response.status === 401) {
                    showMessage('Authorization error. Please check your staff ID or try again.', 'danger');
                } else if (response.status === 404) {
                    showMessage('Service not found. Please check if the backend is running.', 'danger');
                } else {
                    showMessage(data.message || 'Failed to send verification code.', 'danger');
                }
                console.error('OTP request failed with status:', response.status, 'Data:', data);
            }
        } catch (error) {
            showMessage('An error occurred while sending verification code.', 'danger');
            console.error('Error requesting OTP:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        const otpValue = otp.join('');
        if (!email || otpValue.length !== 6) {
            showMessage('Please enter your staff ID and complete 6-digit verification code.', 'danger');
            return;
        }

        setIsLoading(true);
        showMessage('Verifying...', 'info');
        
        try {
            const formattedEmail = formatEmail(email);
            console.log('Attempting login with email:', formattedEmail);
            console.log('API endpoint:', APIAuthLogin);
            
            const response = await fetch(APIAuthLogin, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email: formattedEmail, otp: otpValue })
            });

            console.log('Login response status:', response.status);
            const data = await response.json();
            console.log('Login response data:', data);

            if (response.ok) {
                showMessage(data.message || 'Login successful!', 'success');
                // Handle both old and new token response formats
                if (data.data) {
                    const { token, user } = data.data;
                    
                    // New format with user data
                    if (token && user) {
                        // For now, we'll use the same token as refresh token since backend doesn't return separate refresh token yet
                        login(token, token, user);
                        console.log('Login successful with user data:', user);
                    } 
                    // Legacy format with single token
                    else if (token) {
                        login(token, token);
                        console.log('Login successful with legacy token format');
                    } 
                    else {
                        console.error('Login successful, but no valid tokens received in response data. Data:', data);
                        showMessage('Login successful, but failed to get authentication tokens.', 'warning');
                        return;
                    }
                    
                    // Redirect to main page
                    setTimeout(() => {
                        navigate('/');
                    }, 1000);
                } else {
                    console.error('Login successful, but no data received in response. Response:', data);
                    showMessage('Login successful, but failed to get response data.', 'warning');
                }
            } else {
                // Handle specific error cases
                if (response.status === 401) {
                    showMessage('Invalid staff ID or verification code. Please try again.', 'danger');
                } else if (response.status === 404) {
                    showMessage('Service not found. Please check if the backend is running.', 'danger');
                } else {
                    showMessage(data.message || 'Login failed. Invalid verification code or staff ID.', 'danger');
                }
                console.error('Login failed with status:', response.status, 'Data:', data);
            }
        } catch (error) {
            showMessage('An error occurred during login.', 'danger');
            console.error('Error during login:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setShowOtpSection(false);
        setEmail('');
        setOtp(['', '', '', '', '', '']);
        setMessage('');
        setMessageType('');
    };

    const resendCode = () => {
        setOtp(['', '', '', '', '', '']);
        requestOtp();
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-header">
                    <img src={UnikLog} className='logo-img' alt="UNIK Logo" />
                    <h1 className="login-title">
                        {showOtpSection ? 'Verify your Email' : 'Staff Login'}
                    </h1>
                    {showOtpSection && (
                        <p className="login-subtitle">
                            Enter the code we've sent to your inbox {formatEmail(email)}
                        </p>
                    )}
                </div>

                <form onSubmit={handleLogin} className="login-form">
                    {!showOtpSection ? (
                        <>
                            <div className="input-group">
                                <label htmlFor="email" className="input-label"></label>
                                <div className="email-input-container">
                                    <input
                                        type="text"
                                        className="form-input"
                                        id="email"
                                        value={email}
                                        onChange={handleEmailChange}
                                        placeholder="Enter your UNIK email"
                                        required
                                        disabled={isLoading}
                                    />
                                    <span className="email-suffix">@unik.ac.ug</span>
                                </div>
                            </div>
                            
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={requestOtp}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="loading-spinner"></div>
                                        Sending...
                                    </>
                                ) : (
                                    'Send Verification Code'
                                )}
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="otp-section">                                <div className="otp-inputs">
                                    {otp.map((digit, index) => (
                                        <input
                                            key={index}
                                            id={`otp-${index}`}
                                            type="text"
                                            maxLength="1"
                                            className="otp-input"
                                            value={digit}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                            onPaste={handleOtpPaste}
                                            disabled={isLoading}
                                        />
                                    ))}
                                </div>
                                
                                <div className="otp-actions">
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                        disabled={isLoading || otp.join('').length !== 6}
                                    >
                                        {isLoading ? (
                                            <>
                                                <div className="loading-spinner"></div>
                                                Verifying...
                                            </>
                                        ) : (
                                            'Verify'
                                        )}
                                    </button>
                                </div>
                                
                                <div className="resend-section">
                                    <span>Didn't get the code? </span>
                                    <button
                                        type="button"
                                        className="btn-link"
                                        onClick={resendCode}
                                        disabled={isLoading}
                                    >
                                        Resend it
                                    </button>
                                </div>
                                
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={resetForm}
                                    disabled={isLoading}
                                >
                                    Back to Login
                                </button>
                            </div>
                        </>
                    )}
                </form>
                
                {message && (
                    <div className={`message message-${messageType}`} role="alert">
                        {message}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Login;
